import React, { useContext, useMemo, useState } from "react";

import { Theme, useTheme } from "@mui/material/styles";

import {
  ShippingCart,
  ShippingCartDialogProps,
  ShippingCartItemPrice,
  ShippingCartOrderRequest,
  ShippingCartOrderRequestEntity,
  ShippingCartPrice,
  ShippingCartPriceEntity,
  ShippingCartStatus,
  ShippingCartType,
  ShippingCartViewType,
  StorageOrder,
  StorageOrderStatus,
} from "./ShippingCart.types";
import {
  Alert,
  FullWidthFieldContainer,
  PackableAppContext,
  SingleItemRowContainer,
  SnackbarComponent,
  SnackbarComponentVariant,
  formatLocalizedMoney,
  logDebug,
  mergeProperties,
  packableDeepClone,
} from "packable-components";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import { ShippingCartView } from "./ShippingCartView";

import "./ShippingCart.css";
import { PackableStorageClientContext } from "../context/PackableStorageClientContext.types";
import { Invoice, InvoiceItem, SquarePaymentForm, ValidationErrors } from "packable-common";
import { InvoiceView } from "./InvoiceView";

import { config } from "../../config";

const getStyles = (theme: Theme) => ({
  ShippingCartDetailsContainer: {},
  ShippingCartDetailsDialog: {},
  ShippingCartDetailsShowSpinner: {
    zIndex: 1500,
    top: "calc(50% - 20px)",
    left: "calc(50% - 20px)",
    margin: "0 auto",
    position: "absolute",
    display: "block",
  },
  ShippingCartDetailsHideSpinner: {
    display: "none",
  },
  ShippingCartDetailsDialogTitle: {},
  ShippingCartDetailsDialogTitleContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    pl: 1,
  },
  ShippingCartDetailsDialogContent: {},
  ShippingCartDetailsDialogContentContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  ShippingCartDetailsDialogRowContainer: {
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "center",
  },
  ShippingCartDetailsDialogSingleItemCenteredRowContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  ShippingCartDetailsDialogContentFullWidthTextMultiFieldContainer: {
    width: "100%",
    padding: 1,
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignContent: "center",
    alignItems: "baseline",
  },
  ShippingCartDetailsDialogContentFullWidthTextField: {
    width: "100%",
  },
  ShippingCartDetailsDialogActions: {
    margin: 3,
  },
  ShippingCartDetailsDialogActionsContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  ShippingCartDetailsDialogActionRow: {
    padding: 1,
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    alignContent: "center",
  },
  ShippingCartDetailsDialogActionsCloseButton: {
    muiColor: "primary",
    muiVariant: "outlined",
  },
  ShippingCartDetailsDialogActionsClearButton: {
    muiColor: "secondary",
    muiVariant: "outlined",
  },
  ShippingCartDetailsDialogActionsCheckoutButton: {
    muiColor: "primary",
    muiVariant: "contained",
  },
  PackableAlertDialogBody: {
    display: "flex",
    flexDirection: "column",
    alignContent: "center",
    justifyContent: "center",
    width: "300px",
    padding: "0.75rem",
  },
  PackableAlertDialogTitleTypography: {
    muiVariant: "h6",
  },
  PackableAlertDialogOption1Button: {
    muiVariant: "contained",
  },
  PackableShippingCartDialogOrderAcceptedAlertContent: {},
  ShippingCartDetailsDialogOrderNumberLabelText: {
    pr: 1,
  },
});

export const ShippingCartDialog: React.FC<ShippingCartDialogProps> = (
  props
): React.JSX.Element => {
  const {
    showShippingCart,
    handleCloseShippingCart,
    onOrderCompleted,
    userAppState,
    windowWidth = 800,
    windowHeight = 800,
    styles,
  } = props;

  logDebug("ShippingCartDialog PROPS");
  logDebug(props);

  const theme = useTheme();

  const defaultStyles = useMemo(() => getStyles(theme), [theme]);
  const effectiveStyles = useMemo(
    () =>
      styles === undefined
        ? defaultStyles
        : mergeProperties(defaultStyles, styles),
    [defaultStyles, styles]
  );

  logDebug("ShippingCartDialog effectiveStyles");
  logDebug(effectiveStyles);

  const { httpService } = React.useContext(PackableAppContext);

  const { shippingCart, onShippingCartUpdate } = useContext(
    PackableStorageClientContext
  );

  const [displaySnackbar, setDisplaySnackbar] = useState<boolean>(false);
  const [snackbarVariant, setSnackbarVariant] =
    useState<SnackbarComponentVariant>(SnackbarComponentVariant.info);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [closeAfterMessage, setColoseAfterMessage] = useState<boolean>(false);
  const [displaySpinner, setDisplaySpinner] = useState<boolean>(false);
  const [currentView, setCurrentView] = useState<ShippingCartViewType>(
    ShippingCartViewType.CART_VIEW
  );
  const [validationErrorsList, setValidationErrorsList] = useState<
    Array<ValidationErrors>
  >([]);
  const [showValidationErrorsWarning, setValidationErrorWarning] =
    useState<boolean>(false);
  const [validationErrorCount, setValidationErrorCount] = useState<number>(0);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [price, setPrice] = useState<ShippingCartPrice | null>(null);
  const [order, setOrder] = useState<StorageOrder | null>(null);
  const [processing, setProcessing] = useState<boolean>(false);

  const quoteURL = "/quote/storage";
  const orderURL = "/order";

  const onDialogClose = (
    event: {},
    reason: "backdropClick" | "escapeKeyDown"
  ): void => {
    if (reason === "backdropClick") {
      return;
    }
    handleCloseShippingCart();
  };

  const onValidationErrorsListUpdate = (
    errors: Array<ValidationErrors>
  ): void => {
    setValidationErrorsList(errors);
  };

  const clearCart = (): void => {
    setValidationErrorsList([]);
    let updatedCart: ShippingCart = packableDeepClone(shippingCart);
    updatedCart.cartItems = [];
    updatedCart.cartStatus = ShippingCartStatus.INACTIVE;
    onShippingCartUpdate(updatedCart);
  };

  const handleClearCart = (): void => {
    clearCart();
    handleCloseShippingCart();
  };

  const handleGoBackToCart = () => {
    setCurrentView(ShippingCartViewType.CART_VIEW);
  };

  const handleCloseInvoiceView = () => {
    if (currentView === ShippingCartViewType.ORDER_VIEW && onOrderCompleted) {
      onOrderCompleted();
    }
    setCurrentView(ShippingCartViewType.CART_VIEW);
    handleCloseShippingCart();
  };

  const onPaymentToken = (token: string): void => {
    setProcessing(true);
    createOrder(shippingCart, invoice, token);
  };

  //////////////////
  //// Snackbar ////
  //////////////////
  // display snackbar message
  const displayMessage = (
    variant: SnackbarComponentVariant,
    message: string,
    flag: boolean = false
  ): void => {
    setSnackbarVariant(variant);
    setSnackbarMessage(message);
    setDisplaySnackbar(true);
    setColoseAfterMessage(flag);
  };
  // close snackbar
  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ): void => {
    if (reason === "clickaway") {
      return;
    }
    if (closeAfterMessage) {
      handleCloseShippingCart();
    }
    setDisplaySnackbar(false);
  };
  //////////////////////
  //// END Snackbar ////
  //////////////////////

  ///////////////////////
  //// API callbacks ////
  ///////////////////////
  const onGetQuoteSuccess = (data: any) => {
    const invoice: Invoice = data.data;
    const shippingCartItemPrices: Array<ShippingCartItemPrice> = getItemPrices(
      invoice.invoiceItems || []
    ).sort(
      (a, b) =>
        Number(a.itemLabel.substring(3)) - Number(b.itemLabel.substring(3))
    );
    let shippingCartPrice: ShippingCartPrice = new ShippingCartPriceEntity();
    shippingCartPrice.cartType = shippingCart.cartType;
    shippingCartPrice.currency = invoice.currency;
    shippingCartPrice.subTotal = shippingCartItemPrices.reduce(
      (total, item) => {
        return total + item.subTotal;
      },
      0
    );
    shippingCartPrice.tax = shippingCartItemPrices.reduce((total, item) => {
      return total + item.tax;
    }, 0);
    shippingCartPrice.total = shippingCartItemPrices.reduce((total, item) => {
      return total + item.total;
    }, 0);
    shippingCartPrice.itemPrices = shippingCartItemPrices;
    setInvoice(invoice);
    setPrice(shippingCartPrice);
    setCurrentView(ShippingCartViewType.INVOICE_VIEW);
  };
  const onGetQuoteError = (message: string, data: any) => {
    if (displayMessage) {
      displayMessage(SnackbarComponentVariant.error, message);
    }
  };
  const onCreateOrderSuccess = (data: any) => {
    setProcessing(false);
    clearCart();
    setOrder(data.data);
    setCurrentView(ShippingCartViewType.ORDER_VIEW);
    //setShowOrderAcceptedDialog(true);
  };
  const onCreateOrderError = (message: string, data: any) => {
    setProcessing(false);
    if (displayMessage) {
      displayMessage(SnackbarComponentVariant.error, message);
    }
  };
  ///////////////////////
  //// END API callbacks ////
  ///////////////////////

  ///////////////////
  //// API calls ////
  ///////////////////
  const getQuote = (cart: ShippingCart) => {
    httpService.post(
      quoteURL,
      cart,
      onGetQuoteSuccess,
      onGetQuoteError,
      setDisplaySpinner
    );
  };
  const createOrder = (
    cart: ShippingCart,
    provisionalInvoice: Invoice | null,
    token: string
  ) => {
    let request: ShippingCartOrderRequest =
      new ShippingCartOrderRequestEntity();
    request.shippingCart = cart;
    request.provisionalInvoice = provisionalInvoice;
    request.token = token;
    httpService.post(
      orderURL,
      request,
      onCreateOrderSuccess,
      onCreateOrderError,
      setDisplaySpinner
    );
  };
  ///////////////////////
  //// END API calls ////
  ///////////////////////

  const onAttributeValidationFailedWarninigClose = () => {
    setValidationErrorWarning(false);
    setValidationErrorCount(0);
  };

  const onShippingCartCheckout = () => {
    const validationErrorCount = validationErrorsList.reduce(
      (sum, subArray) => sum + subArray.errors.length,
      0
    );
    if (validationErrorCount > 0) {
      setValidationErrorCount(validationErrorCount);
      setValidationErrorWarning(true);
    } else {
      shippingCart.inventoryId = userAppState?.currentInventoryId;
      getQuote(shippingCart);
    }
  };

  const onPayment = () => {
    setCurrentView(ShippingCartViewType.PAYMENT_VIEW);
  };

  const onSubmitOrder = () => {
    shippingCart.inventoryId = userAppState?.currentInventoryId;
    createOrder(shippingCart, invoice, "NO_TOKEN");
  };

  const getItemPrices = (
    invoices: Array<InvoiceItem>
  ): Array<ShippingCartItemPrice> => {
    const map = new Map<string, ShippingCartItemPrice>();
    for (const invoice of invoices) {
      const existingPrice = map.get(invoice.itemId || "");
      if (existingPrice) {
        existingPrice.subTotal += invoice.subTotal;
        existingPrice.tax += invoice.tax;
        existingPrice.total += invoice.total;
      } else {
        const shippingCartItem = shippingCart.cartItems?.find(
          (ci) => ci.itemId === invoice.itemId
        );
        if (shippingCartItem !== undefined) {
          map.set(invoice.itemId || "", {
            itemId: invoice.itemId || "",
            itemLabel: shippingCartItem.itemLabel,
            storageEndDate: shippingCartItem.storageEndDate || new Date(),
            currency: invoice.currency,
            subTotal: invoice.subTotal,
            tax: invoice.tax,
            total: invoice.total,
          });
        }
      }
    }
    return Array.from(map.values());
  };

  const paymentRequired = (): boolean => {
    let rc = true;
    if (price?.total === 0) {
      rc = false;
    }
    return rc;
  };

  const multipleItems = (): boolean => {
    /*
    if (order !== undefined && order !== null) {
      return order.order.orderItems.length > 1;
    }
      */
    return false;
  };

  const dialogTitle = (): string => {
    if (currentView === ShippingCartViewType.CART_VIEW) {
      return shippingCart.cartType === ShippingCartType.STORAGE
        ? "Home to Storage Shipping Cart"
        : "Storage to Home Shipping Cart";
    } else if (currentView === ShippingCartViewType.INVOICE_VIEW) {
      return shippingCart.cartType === ShippingCartType.STORAGE
        ? "Storage Price"
        : "Storage To Home Shipping Price";
    } else if (currentView === ShippingCartViewType.PAYMENT_VIEW) {
      return "Storage Order Payment";
    } else if (currentView === ShippingCartViewType.ORDER_VIEW) {
      return shippingCart.cartType === ShippingCartType.STORAGE
        ? "Storage Order"
        : "Storage To Home Shipping Order";
    }
    return "";
  };

  const renderAttributeValidationFailedWarningDialog = () => {
    const title = `${dialogTitle()} Validation`;
    const message = `We found ${validationErrorCount} issues with your shipping cart. These issues are highlighted in red. Please correct the issues or remove the affected items from your cart before checking out`;
    return (
      <Alert
        isShowing={showValidationErrorsWarning}
        title={title}
        onOption1={onAttributeValidationFailedWarninigClose}
        option1Label="Close"
        styles={effectiveStyles}
      >
        <div style={effectiveStyles.PackableAlertDialogBody}>
          <Typography>{message}</Typography>
        </div>
      </Alert>
    );
  };

  const renderCartView = () => {
    return (
      <>
        {renderAttributeValidationFailedWarningDialog()}
        {shippingCart.cartType === ShippingCartType.STORAGE && (
          <SingleItemRowContainer
            styles={{
              PackableSingleItemRowContainer: { alignContent: "center" },
            }}
          >
            <FullWidthFieldContainer>
              <Grid
                className="ShippingCartDetailsDialogContentFullWidthTextField"
                sx={
                  effectiveStyles.ShippingCartDetailsDialogContentFullWidthTextField
                }
              >
                Please tell us how long you would like to store your items.
                Also, please verify the dimensions and weight of each item. Note
                that if the dimensions or weight provided are inaccurate, the
                shipping carrier may refuse pickup.
              </Grid>
            </FullWidthFieldContainer>
          </SingleItemRowContainer>
        )}
        <ShippingCartView
          displayMessage={displayMessage}
          setDisplaySpinner={setDisplaySpinner}
          userAppState={userAppState}
          handleCloseShippingCart={handleCloseShippingCart}
          shippingCart={shippingCart}
          onShippingCartUpdate={onShippingCartUpdate}
          validationErrorsList={validationErrorsList}
          onValidationErrorsListUpdate={onValidationErrorsListUpdate}
          styles={effectiveStyles}
        />
      </>
    );
  };

  const renderInvoiceView = () => {
    if (price !== null) {
      return (
        <SingleItemRowContainer
          styles={{
            PackableSingleItemRowContainer: { alignContent: "center" },
          }}
        >
          <InvoiceView shippingCartPrice={price} styles={effectiveStyles} />
        </SingleItemRowContainer>
      );
    } else {
      return <></>;
    }
  };

  const renderPaymentView = () => {
    return (
      <>
        {invoice && !processing ? (
          <>
            <Grid
              className="ShippingCartDetailsDialogSingleItemCenteredRowContainer"
              sx={
                effectiveStyles.ShippingCartDetailsDialogSingleItemCenteredRowContainer
              }
            >
              <FullWidthFieldContainer>
                <Grid
                  className="ShippingCartDetailsDialogContentFullWidthTextField"
                  sx={
                    effectiveStyles.ShippingCartDetailsDialogContentFullWidthTextField
                  }
                >
                  <Typography variant="h6">
                    {`Pay ${formatLocalizedMoney(
                      invoice?.total || 0,
                      2,
                      invoice?.currency,
                      Intl.DateTimeFormat().resolvedOptions().locale
                    )}`}
                  </Typography>
                </Grid>
              </FullWidthFieldContainer>
            </Grid>
            <Grid
              className="ShippingCartDetailsDialogSingleItemCenteredRowContainer"
              sx={
                effectiveStyles.ShippingCartDetailsDialogSingleItemCenteredRowContainer
              }
            >
              <FullWidthFieldContainer>
                <SquarePaymentForm
                  amount={invoice?.total}
                  currency={invoice?.currency}
                  displayMessage={displayMessage}
                  onPaymentToken={onPaymentToken}
                  applicationId={config.APPLICATION_ID}
                  locationId={config.LOCATION_ID}
                  styles={effectiveStyles}
                />
              </FullWidthFieldContainer>
            </Grid>
          </>
        ) : (
          <SingleItemRowContainer
            styles={{
              PackableSingleItemRowContainer: { alignContent: "center" },
            }}
          >
            <FullWidthFieldContainer>
              <Grid
                className="ShippingCartDetailsDialogContentFullWidthTextField"
                sx={
                  effectiveStyles.ShippingCartDetailsDialogContentFullWidthTextField
                }
              >
                Processing... Please don't navigate away from this page until
                the processing is completed
              </Grid>
            </FullWidthFieldContainer>
          </SingleItemRowContainer>
        )}
      </>
    );
  };

  const renderOrderView = () => {
    if (order !== null) {
      return (
        <>
          {order.status === StorageOrderStatus.SUCCESS && (
            <>
              <SingleItemRowContainer
                styles={{
                  PackableSingleItemRowContainer: { alignContent: "center" },
                }}
              >
                <FullWidthFieldContainer>
                  <Grid
                    className="ShippingCartDetailsDialogContentFullWidthTextField"
                    sx={
                      effectiveStyles.ShippingCartDetailsDialogContentFullWidthTextField
                    }
                  >
                    Your order has been accepted!
                  </Grid>
                </FullWidthFieldContainer>
              </SingleItemRowContainer>
              <SingleItemRowContainer
                styles={{
                  PackableSingleItemRowContainer: { alignContent: "center" },
                }}
              >
                <Grid
                  className="ShippingCartDetailsDialogContentFullWidthTextMultiFieldContainer"
                  sx={
                    effectiveStyles.ShippingCartDetailsDialogContentFullWidthTextMultiFieldContainer
                  }
                >
                  <Grid
                    className="ShippingCartDetailsDialogOrderNumberLabelText"
                    sx={
                      effectiveStyles.ShippingCartDetailsDialogOrderNumberLabelText
                    }
                  >
                    Order Number:
                  </Grid>
                  <Grid>
                    <Typography variant="h6">
                      {order.order.orderNumber}
                    </Typography>
                  </Grid>
                </Grid>
              </SingleItemRowContainer>
              {shippingCart.cartType === ShippingCartType.STORAGE && (
                <SingleItemRowContainer
                  styles={{
                    PackableSingleItemRowContainer: { alignContent: "center" },
                  }}
                >
                  <FullWidthFieldContainer>
                    <Grid
                      className="ShippingCartDetailsDialogContentFullWidthTextField"
                      sx={
                        effectiveStyles.ShippingCartDetailsDialogContentFullWidthTextField
                      }
                    >
                      {`We will send you a shipping label${
                        multipleItems() ? "s" : ""
                      } for your item${
                        multipleItems() ? "s" : ""
                      }. Once you receive it and are ready to ship your item${
                        multipleItems() ? "s" : ""
                      }, you can request an item pickup in the application.`}
                    </Grid>
                  </FullWidthFieldContainer>
                </SingleItemRowContainer>
              )}
            </>
          )}
          {order.status === StorageOrderStatus.PAYMENT_FAILED && (
            <>
              <SingleItemRowContainer
                styles={{
                  PackableSingleItemRowContainer: { alignContent: "center" },
                }}
              >
                <FullWidthFieldContainer>
                  <Grid
                    className="ShippingCartDetailsDialogContentFullWidthTextField"
                    sx={
                      effectiveStyles.ShippingCartDetailsDialogContentFullWidthTextField
                    }
                  >
                    We failed to process your payment. Please try again later.
                  </Grid>
                </FullWidthFieldContainer>
              </SingleItemRowContainer>
            </>
          )}
        </>
      );
    } else {
      return <></>;
    }
  };

  const renderCartViewActions = () => {
    return (
      <>
        <Grid
          className="ShippingCartDetailsDialogActionRow"
          sx={effectiveStyles.ShippingCartDetailsDialogActionRow}
        >
          <Tooltip title="Clear Shipping Cart">
            <Button
              className="ShippingCartDetailsDialogActionsClearButton"
              sx={{
                ...effectiveStyles.ShippingCartDetailsDialogActionsClearButton,
                mr: 2,
              }}
              onClick={handleClearCart}
              color={
                effectiveStyles.ShippingCartDetailsDialogActionsClearButton
                  .muiColor
              }
              variant={
                effectiveStyles.ShippingCartDetailsDialogActionsClearButton
                  .muiVariant
              }
            >
              Clear Cart
            </Button>
          </Tooltip>
          <Tooltip title="Checkout Shipping Cart">
            <Button
              className="ShippingCartDetailsDialogActionsCheckoutButton"
              sx={
                effectiveStyles.ShippingCartDetailsDialogActionsCheckoutButton
              }
              onClick={onShippingCartCheckout}
              color={
                effectiveStyles.ShippingCartDetailsDialogActionsCheckoutButton
                  .muiColor
              }
              variant={
                effectiveStyles.ShippingCartDetailsDialogActionsCheckoutButton
                  .muiVariant
              }
            >
              Checkout
            </Button>
          </Tooltip>
        </Grid>
        <Grid
          className="ShippingCartDetailsDialogActionRow"
          sx={effectiveStyles.ShippingCartDetailsDialogActionRow}
        >
          <Tooltip title="Close Shipping Cart">
            <Button
              className="ShippingCartDetailsDialogActionsCloseButton"
              sx={effectiveStyles.ShippingCartDetailsDialogActionsCloseButton}
              onClick={handleCloseShippingCart}
              color={
                effectiveStyles.ShippingCartDetailsDialogActionsCloseButton
                  .muiColor
              }
              variant={
                effectiveStyles.ShippingCartDetailsDialogActionsCloseButton
                  .muiVariant
              }
            >
              Close
            </Button>
          </Tooltip>
        </Grid>
      </>
    );
  };

  const renderInvoiceViewActions = () => {
    return (
      <>
        <Grid
          className="ShippingCartDetailsDialogActionRow"
          sx={effectiveStyles.ShippingCartDetailsDialogActionRow}
        >
          <Tooltip title="Close Shipping Cart">
            <Button
              className="ShippingCartDetailsDialogActionsCloseButton"
              sx={{
                ...effectiveStyles.ShippingCartDetailsDialogActionsCloseButton,
                mr: 2,
              }}
              onClick={handleCloseInvoiceView}
              color={
                effectiveStyles.ShippingCartDetailsDialogActionsCloseButton
                  .muiColor
              }
              variant={
                effectiveStyles.ShippingCartDetailsDialogActionsCloseButton
                  .muiVariant
              }
            >
              Close
            </Button>
          </Tooltip>
          <Tooltip title="Back to Shipping Cart">
            <Button
              className="ShippingCartDetailsDialogActionsCloseButton"
              sx={{
                ...effectiveStyles.ShippingCartDetailsDialogActionsCloseButton,
                mr: 2,
              }}
              onClick={handleGoBackToCart}
              color={
                effectiveStyles.ShippingCartDetailsDialogActionsCloseButton
                  .muiColor
              }
              variant={
                effectiveStyles.ShippingCartDetailsDialogActionsCloseButton
                  .muiVariant
              }
            >
              Back
            </Button>
          </Tooltip>
          {paymentRequired() ? (
            <Tooltip title="Proceed to Payment">
              <Button
                className="ShippingCartDetailsDialogActionsCheckoutButton"
                sx={
                  effectiveStyles.ShippingCartDetailsDialogActionsCheckoutButton
                }
                onClick={onPayment}
                color={
                  effectiveStyles.ShippingCartDetailsDialogActionsCheckoutButton
                    .muiColor
                }
                variant={
                  effectiveStyles.ShippingCartDetailsDialogActionsCheckoutButton
                    .muiVariant
                }
              >
                Payment
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title="Submit Order">
              <Button
                className="ShippingCartDetailsDialogActionsCheckoutButton"
                sx={
                  effectiveStyles.ShippingCartDetailsDialogActionsCheckoutButton
                }
                onClick={onSubmitOrder}
                color={
                  effectiveStyles.ShippingCartDetailsDialogActionsCheckoutButton
                    .muiColor
                }
                variant={
                  effectiveStyles.ShippingCartDetailsDialogActionsCheckoutButton
                    .muiVariant
                }
              >
                Submit Order
              </Button>
            </Tooltip>
          )}
        </Grid>
      </>
    );
  };

  const renderPaymentViewActions = () => {
    return (
      <>
        <Grid
          className="ShippingCartDetailsDialogActionRow"
          sx={effectiveStyles.ShippingCartDetailsDialogActionRow}
        >
          <Tooltip title="Close Shipping Cart">
            <Button
              className="ShippingCartDetailsDialogActionsCloseButton"
              sx={{
                ...effectiveStyles.ShippingCartDetailsDialogActionsCloseButton,
                mr: 2,
              }}
              disabled={processing}
              onClick={handleCloseInvoiceView}
              color={
                effectiveStyles.ShippingCartDetailsDialogActionsCloseButton
                  .muiColor
              }
              variant={
                effectiveStyles.ShippingCartDetailsDialogActionsCloseButton
                  .muiVariant
              }
            >
              Close
            </Button>
          </Tooltip>
          <Tooltip title="Back to Shipping Cart">
            <Button
              className="ShippingCartDetailsDialogActionsCloseButton"
              sx={effectiveStyles.ShippingCartDetailsDialogActionsCloseButton}
              disabled={processing}
              onClick={handleGoBackToCart}
              color={
                effectiveStyles.ShippingCartDetailsDialogActionsCloseButton
                  .muiColor
              }
              variant={
                effectiveStyles.ShippingCartDetailsDialogActionsCloseButton
                  .muiVariant
              }
            >
              Back
            </Button>
          </Tooltip>
        </Grid>
      </>
    );
  };

  const renderOrderViewActions = () => {
    return (
      <>
        <Grid
          className="ShippingCartDetailsDialogActionRow"
          sx={effectiveStyles.ShippingCartDetailsDialogActionRow}
        >
          <Tooltip title="Close Shipping Cart">
            <Button
              className="ShippingCartDetailsDialogActionsCloseButton"
              sx={effectiveStyles.ShippingCartDetailsDialogActionsCloseButton}
              disabled={processing}
              onClick={handleCloseInvoiceView}
              color={
                effectiveStyles.ShippingCartDetailsDialogActionsCloseButton
                  .muiColor
              }
              variant={
                effectiveStyles.ShippingCartDetailsDialogActionsCloseButton
                  .muiVariant
              }
            >
              Close
            </Button>
          </Tooltip>
        </Grid>
      </>
    );
  };

  return (
    <div
      className="ShippingCartDetailsContainer"
      style={effectiveStyles.ShippingCartDetailsContainer}
    >
      <Dialog
        className="ShippingCartDetailsDialog"
        sx={effectiveStyles.ShippingCartDetailsDialog}
        open={showShippingCart}
        onClose={onDialogClose}
        aria-labelledby="form-dialog-title"
        maxWidth={"md"}
        fullScreen={
          windowWidth < 500 //|| windowHeight < 500
        }
      >
        <DialogTitle
          className="ShippingCartDetailsDialogTitle"
          sx={effectiveStyles.ShippingCartDetailsDialogTitle}
          id="form-dialog-title"
        >
          <Grid
            className="ShippingCartDetailsDialogTitleContainer"
            sx={effectiveStyles.ShippingCartDetailsDialogTitleContainer}
          >
            <Box
              className="ShippingCartDetailsTitle"
              sx={effectiveStyles.ShippingCartDetailsTitle}
            >
              {dialogTitle()}
            </Box>
          </Grid>
        </DialogTitle>
        <DialogContent
          className="ShippingCartDetailsDialogContent"
          sx={effectiveStyles.ShippingCartDetailsDialogContent}
        >
          <SnackbarComponent
            openFlag={displaySnackbar}
            variant={snackbarVariant}
            message={snackbarMessage}
            onClose={handleCloseSnackbar}
          />
          <div
            style={
              displaySpinner
                ? effectiveStyles.ShippingCartDetailsShowSpinner
                : effectiveStyles.ShippingCartDetailsHideSpinner
            }
          >
            <CircularProgress />
          </div>
          <Grid
            className="ShippingCartDetailsDialogContentContainer"
            sx={effectiveStyles.ShippingCartDetailsDialogContentContainer}
          >
            {currentView === ShippingCartViewType.CART_VIEW && renderCartView()}
            {currentView === ShippingCartViewType.INVOICE_VIEW &&
              renderInvoiceView()}
            {currentView === ShippingCartViewType.PAYMENT_VIEW &&
              renderPaymentView()}
            {currentView === ShippingCartViewType.ORDER_VIEW &&
              renderOrderView()}
          </Grid>
        </DialogContent>
        <DialogActions
          className="ShippingCartDetailsDialogActions"
          sx={effectiveStyles.ShippingCartDetailsDialogActions}
        >
          <Grid
            container
            className="ShippingCartDetailsDialogActionsContainer"
            sx={effectiveStyles.ShippingCartDetailsDialogActionsContainer}
          >
            {currentView === ShippingCartViewType.CART_VIEW &&
              renderCartViewActions()}
            {currentView === ShippingCartViewType.INVOICE_VIEW &&
              renderInvoiceViewActions()}
            {currentView === ShippingCartViewType.PAYMENT_VIEW &&
              renderPaymentViewActions()}
            {currentView === ShippingCartViewType.ORDER_VIEW &&
              renderOrderViewActions()}
          </Grid>
        </DialogActions>
      </Dialog>
    </div>
  );
};
